import { useDispatch } from 'react-redux';
import { useCurrentPageType } from './UrlManager/pageTypeHooks';
import { PageType, ProductListingPages } from './UrlManager/types';
import useRouter from './useRouter';
import { hideOverlay, showOverlay } from 'Client/redux/header/overlays/actions';
import { OverlayId } from 'Client/components/Overlays/types';
import { OverlayOptions } from 'Client/redux/header/overlays/types';
import useGa from 'Client/hooks/useGa';
import { useBellaSelector } from 'Client/redux/hooks';
import { getOverlayById } from 'Client/redux/header/overlays/selectors';
import { OverlayIds } from 'Common/appshell_types/customEventTypes';
import { LOGIN_REGISTER_OVERLAY_EVENTS } from 'Common/ga/types';

const {
    REGISTRATION_OVERLAY_HIDE,
    LOGIN_OVERLAY_CATEGORY,
    REGISTRATION_OVERLAY_CATEGORY,
    PARTIAL_LOGIN_OVERLAY_CATEGORY,
    REGISTRATION_OVERLAY_SHOWN,
    PARTIAL_LOGIN_OVERLAY_DISMISSED,
    LOGIN_OVERLAY_DISMISSED,
    PARTIAL_LOGIN_OVERLAY_SHOWN,
    LOGIN_OVERLAY_SHOWN,
} = LOGIN_REGISTER_OVERLAY_EVENTS;

export const REGISTRATION_OVERLAY_TRACKING_NAMES = {
    accountBtn: 'My account header',
    wishlistBtn: 'Wishlist header',
};

const useLoginAndRegisterOverlayWithTracking = () => {
    const dispatch = useDispatch();
    const gaTracking = useGa();
    const { pageType } = useCurrentPageType();
    const {
        location: { pathname, search },
    } = useRouter();
    const overlays = useBellaSelector(state => state.header.overlays);
    const sessionType = useBellaSelector(state => state.appShellLogin.data?.sessionType);

    const isLoginForm = () => {
        return getOverlayById(overlays, OverlayIds.LOGIN_AND_REGISTER)?.options?.isLogin;
    };

    const trackLoginOverlayShow = (trackingParams?: {
        forceTracking?: boolean;
        isLogin?: boolean;
        origin?: keyof typeof REGISTRATION_OVERLAY_TRACKING_NAMES | string;
    }) => {
        const track = () => {
            let label = '-';
            if (trackingParams?.origin && sessionType === 'partial') {
                if (trackingParams.origin === 'accountBtn') {
                    label = 'My account header';
                } else if (trackingParams.origin === 'wishlistBtn') {
                    label = 'Wishlist header';
                }
                gaTracking.trackGenericEvent(PARTIAL_LOGIN_OVERLAY_CATEGORY, {
                    action: PARTIAL_LOGIN_OVERLAY_SHOWN,
                    label,
                });
                return;
            }

            gaTracking.trackGenericEvent(LOGIN_OVERLAY_CATEGORY, {
                action: LOGIN_OVERLAY_SHOWN,
                label,
            });
        };

        if (trackingParams?.forceTracking) {
            track();
        } else if (trackingParams?.isLogin) {
            track();
        }
    };

    const trackLoginOverlayDismiss = (origin?: 'closeBtn' | 'overlay' | 'footerLink') => {
        let label = '-';
        if (origin === 'closeBtn') {
            label = 'Click on "X"';
        }
        if (origin === 'overlay') {
            label = 'Background click';
        }
        if (origin === 'footerLink') {
            label = isLoginForm() ? 'Registration link click' : 'Login link click';
        }

        if (!isLoginForm()) {
            gaTracking.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
                action: REGISTRATION_OVERLAY_HIDE,
                label,
            });
            return;
        }

        if (sessionType === 'partial') {
            gaTracking.trackGenericEvent(PARTIAL_LOGIN_OVERLAY_CATEGORY, {
                action: PARTIAL_LOGIN_OVERLAY_DISMISSED,
                label,
            });
            return;
        }

        gaTracking.trackGenericEvent(LOGIN_OVERLAY_CATEGORY, {
            action: LOGIN_OVERLAY_DISMISSED,
            label,
        });
    };

    const trackLoginAction = (label: string) => {
        const options = {
            action: 'Login',
            label,
        };

        if (isLoginForm()) {
            if (sessionType === 'partial') {
                gaTracking.trackGenericEvent(PARTIAL_LOGIN_OVERLAY_CATEGORY, options);
                return;
            }
            gaTracking.trackGenericEvent(LOGIN_OVERLAY_CATEGORY, options);
        }
    };

    const trackLoginAndRegisterOverlayFomWishlist = (sku: string) => {
        let currentPage: string | undefined;
        if (pageType === PageType.PDP) {
            currentPage = 'PDP';
        } else if (pageType === PageType.LOOKS) {
            currentPage = 'LDP';
        } else if (pageType === PageType.HOMEPAGE) {
            currentPage = 'Homepage';
        } else if (ProductListingPages.includes(pageType)) {
            currentPage = 'PLP';
        }
        const url = `${pathname}${search}`;
        if (currentPage) {
            gaTracking.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
                action: REGISTRATION_OVERLAY_SHOWN,
                label: `Add to Wishlist,${currentPage},${sku},${url}`,
            });
        }
    };

    const trackRegistrationOverlayShow = (origin: keyof typeof REGISTRATION_OVERLAY_TRACKING_NAMES) => {
        gaTracking.trackGenericEvent(REGISTRATION_OVERLAY_CATEGORY, {
            action: REGISTRATION_OVERLAY_SHOWN,
            label: REGISTRATION_OVERLAY_TRACKING_NAMES[origin],
        });
    };

    const showLoginAndRegisterOverlay = (options?: OverlayOptions) => {
        dispatch(showOverlay(OverlayId.LOGIN_AND_REGISTER, options));
    };

    const hideLoginAndRegisterOverlay = (isBlockedUser: boolean, origin?: 'closeBtn' | 'overlay') => {
        dispatch(hideOverlay(OverlayId.LOGIN_AND_REGISTER));
        if (!isBlockedUser) {
            trackLoginOverlayDismiss(origin);
        }
    };

    return {
        showLoginAndRegisterOverlay,
        hideLoginAndRegisterOverlay,
        trackLoginOverlayShow,
        trackLoginOverlayDismiss,
        trackLoginAction,
        trackLoginAndRegisterOverlayFomWishlist,
        trackRegistrationOverlayShow,
    };
};

export default useLoginAndRegisterOverlayWithTracking;
